import { React, bind, _ } from 'Imports';
import { Button, CircularProgress } from 'MaterialUIComponents';
import {
    BulkRequest,
    BulkRatingRequest,
    VideoEventFullListResponse,
    VideoEventResponse,
    AssignDriverToEventsRequest,
    VideoEventWorkflowStatusIdEnum,
} from '$Generated/api';
import { DeleteConfirmation } from '$Components/Shared/DeleteConfirmation';
import { DismissConfirmation } from '$Components/Shared/DismissConfirmation';
import { forEach } from 'lodash';
import { getSplitUserAttributes, isSplitTreatmentOn, TypedSplitTreatments, splitTreatmentNames } from '$Utilities/split-io-sdk';
import { IConfigServiceInjectedProps, ConfigService } from '$State/ConfigFreezerService';
import { IPricingPlanServiceInjectedProps, PricingPlanService } from '$State/PricingPlanFreezerService';
import { IVideoEventServiceInjectedProps, VideoEventService } from '$State/VideoEventFreezerService';
import {
    IVideoEventFilterServiceMultiselectInjectedProps,
    VideoEventFilterServiceMultiselect,
} from '$State/VideoEventFilterFreezerServiceMultiselect';
import { ISplitTreatmentsChildProps } from '@splitsoftware/splitio-react/types/types';
import { IntegrationPartnerDataService, IIntegrationPartnerDataInjectedProps } from '$State/IntegrationPartnerDataFreezerService';
import { RolesEnum, canEditByRole } from '$Externals/VerifyRole';
import { VideoEventCardList } from '$Components/VideoEvents/VideoEventCardList/VideoEventCardList';
import { VideoEventListMultiSelect } from '$Components/VideoEvents/VideoEventList/VideoEventListMultiSelect';
import ConfigurableFilterBar, { FilterConfig, FilterType } from '$Components/FilterComponents/ConfigurableFilterBar';
import { isEncompass } from '$Utilities/integrationPlatform';
import { AlertMessage } from '$Components/Shared/AlertMessage';
import { AlertTypes } from '$Utilities/Enums/AlertTypes';
import { TermsOfUse } from '$Components/Shared/TermsOfUse';
import { PageHeader } from '$Components/Shared/PageHeader';
import VideoEventHeaderSubText from '$Components/Shared/VideoEventHeaderSubText';
import { FiltersButton, ViewModeToggleButton } from '$Components/FilterComponents/Buttons';
import VideoEventExportComponent from '$Components/VideoEvents/VideoEventExport/VideoEventExportComponent';
import {
    FlaggedStatusOptions,
    getFormattedCompanyLevels,
    getFormattedDeviceResults,
    getFormattedDrivers,
    getFormattedEventTypes,
    VideoEventScoreOptions,
} from '$Utilities/filterUtilities';
import CompanyLevelFilter from '$Components/FilterComponents/Filters/CompanyLevelFilter';
import { toDate } from '$Utilities/dateUtility';
import { getVideoEventStatusDropdownList } from '$Utilities/videoEventStatusUtility';

const styles = require('$Pages/VideoEvents/VideoEvents.scss') as {
    main: string;
    showUnprocessedEvents: string;
    noVideos: string;
};

export interface IMultiselectFilterOptions {
    eventTypes?: number[];
    driverNames?: string[];
    startDate?: Date;
    endDate?: Date;
    vehicleIds?: string[];
    showFlaggedOnly?: boolean;
    showFlaggedByUserOnly?: boolean;
    status?: VideoEventWorkflowStatusIdEnum[];
    scoreIds?: number[];
    flaggedStatus?: number[];
    includeInactiveDrivers?: boolean;
    includeInactiveVehicles?: boolean;
    companyLevelIds?: string[];
}

interface IVideoEventsState {
    events: VideoEventResponse[];
    eventIds: VideoEventFullListResponse;
    backdropVisible: boolean;
    showMobileFilterDrawer: boolean;
    isMobileView: boolean;
    shrinkHeight: number;
    hasVideoEventScoring: boolean;
    hasVideoEventStatusWorkflow: boolean;
    showDeletePopup: boolean;
    deleteButtonDisabled: boolean;
    selectedEventIds: number[];
    isBulk: boolean;
    showDismissPopup: boolean;
    showSnackbar: boolean;
    snackbarType: AlertTypes.Success | AlertTypes.Fail;
    snackbarMessage: string;
    groupsFilter: string;
}

interface IVideoEventsBaseProps {
    onSelectVideoEvent: (eventId: string, goBack: boolean) => void;
    isShowInactiveVehiclesSplitOn?: boolean;
    isShowInactiveDriversSplitOn?: boolean;
    isCompanyLevelSplitOn?: boolean;
}

type IVideoEventsProps = IVideoEventsBaseProps &
    IVideoEventServiceInjectedProps &
    IConfigServiceInjectedProps &
    IVideoEventFilterServiceMultiselectInjectedProps &
    IPricingPlanServiceInjectedProps &
    IIntegrationPartnerDataInjectedProps;

let isMounted = false;

class _VideoEventsPage extends React.Component<IVideoEventsProps, IVideoEventsState> {
    videoEventsHeaderDiv: any;
    filterHeaderDiv: any;
    filterBarDiv: any;

    state: IVideoEventsState = {
        events: [],
        eventIds: {},
        backdropVisible: false,
        showMobileFilterDrawer: false,
        isMobileView: false,
        shrinkHeight: 0,
        hasVideoEventScoring: false,
        hasVideoEventStatusWorkflow: false,
        showDeletePopup: false,
        deleteButtonDisabled: false,
        selectedEventIds: [],
        isBulk: false,
        showDismissPopup: false,
        showSnackbar: false,
        snackbarType: AlertTypes.Fail,
        snackbarMessage: '',
        groupsFilter: this.props.integrationPartnerData.getGroupsFilter(),
    };

    async getVideoEvents(): Promise<void> {
        VideoEventService.clearVideoEvents();
        this.props.videoEventFilterMultiselect.setPageNumber(1);
        const eventFilter = _.cloneDeep(this.props.videoEventFilterMultiselect.getVideoEventFilter());

        //set aside the prev saved filters for driver and vehicle
        const prevDriverFilter = eventFilter.driverNames;
        const prevVehicleFilter = eventFilter.vehicleIds;

        //clear up filter vehicle and driver ids the very first time loading video events page.
        eventFilter.driverNames = [];
        eventFilter.vehicleIds = [];

        //get results from integrationPartnerData (this will iclude Geotab groups if necessary), this is what should be
        // loaded in the drop downs.
        const integrationDrivers = this.props.integrationPartnerData.getState().driversResults;
        const integrationVehicles = this.props.integrationPartnerData.getState().deviceResults;

        //compare the prev filters for drivers and vehicles to what is in the integration data for drivers and vehicles.
        //add to the eventFilter only those that are matching
        if (prevDriverFilter?.some((s) => s == '-1')) {
            eventFilter.driverNames?.push('-1');
        }
        forEach(integrationDrivers.data, function (d) {
            if (prevDriverFilter && prevDriverFilter.indexOf(d.id) > -1) {
                eventFilter.driverNames?.push(d.id);
            }
        });
        forEach(integrationVehicles.data, function (v) {
            if (v.id && prevVehicleFilter && prevVehicleFilter.indexOf(v.id) > -1) {
                eventFilter.vehicleIds?.push(v.id);
            }
        });

        if (eventFilter.includeInactiveDrivers) {
            this.props.videoEvents.setIncludeInactiveDrivers(true);
        }
        if (eventFilter.includeInactiveVehicles) {
            this.props.videoEvents.setIncludeInactiveVehicles(true);
        }

        //apply the filters and get the data
        await this.handleFilterEventsMultiselect(eventFilter);
    }

    async getVideoEventRatings(): Promise<void> {
        await VideoEventService.getVideoEventRatings();
    }

    async componentDidMount(): Promise<void> {
        // Subscribe to store updates
        this.props.integrationPartnerData.on('update', this.handleStoreUpdate);

        isMounted = true;

        // if split is off, include inactive vehicles by default
        if (!this.props.isShowInactiveVehiclesSplitOn) {
            this.props.videoEvents.setIncludeInactiveVehicles(true);
        }

        await this.props.integrationPartnerData.getDevices(true);
        await this.props.integrationPartnerData.getDrivers();
        await this.props.videoEvents.getVideoEventTypes();
        await this.getVideoEventRatings();
        await this.getVideoEvents();
        /*
          Due to the fact that this component may mount more than 
          once, we need to check if it is still
          mounted at the time of receiving a result instead
          of using await.
        */
        this.props.pricingPlan.hasFeatureType('VideoEventScoring').then((result) => {
            if (isMounted) {
                this.setState({ hasVideoEventScoring: result });
            }
        });

        this.props.pricingPlan.hasFeatureType('VideoEventStatusWorkflow').then((result) => {
            if (isMounted) {
                this.setState({ hasVideoEventStatusWorkflow: result });
            }
        });
    }

    async componentWillUnmount(): Promise<void> {
        isMounted = false;
        // Unsubscribe from store updates
        this.props.integrationPartnerData.off('update', this.handleStoreUpdate);
    }

    async componentDidUpdate(prevProps: Readonly<IVideoEventsProps>, prevState: Readonly<IVideoEventsState>): Promise<void> {
        if (!_.isEqual(this.state.groupsFilter, prevState.groupsFilter)) {
            await IntegrationPartnerDataService.getDrivers();
            await IntegrationPartnerDataService.getDevices(true);

            //we know the group drivers at this point
            await this.getVideoEvents();
        }
    }

    @bind
    handleStoreUpdate(): void {
        const newGroupsFilter = this.props.integrationPartnerData.getGroupsFilter();
        if (!_.isEqual(this.state.groupsFilter, newGroupsFilter)) {
            this.setState({ groupsFilter: newGroupsFilter });
        }
    }

    @bind
    handleEventLayoutChange(e: any, value: 'tile' | 'list'): void {
        if (value !== null) {
            VideoEventService.clearVideoEvents();
            this.props.videoEventFilterMultiselect.setPageNumber(1);
            this.props.videoEventFilterMultiselect.setSortFields('date', false);
            const eventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();
            this.props.videoEvents.getFilteredEventsMultiselect(eventFilter);
            this.props.videoEvents.setVideoEventViewType(value);
        }
    }

    @bind
    toggleFilters(): void {
        const { showMobileFilterDrawer: filtersVisible } = this.state;
        this.setState({
            showMobileFilterDrawer: !filtersVisible,
        });
    }

    @bind
    async handleFilterEventsMultiselect(filterOptions: IMultiselectFilterOptions): Promise<void> {
        if (isMounted) {
            this.props.videoEvents.clearVideoEvents();
            this.props.videoEventFilterMultiselect.setPageNumber(1);
            this.props.videoEventFilterMultiselect.updateFilterOptions(
                filterOptions.eventTypes,
                filterOptions.driverNames,
                filterOptions.vehicleIds,
                filterOptions.startDate,
                filterOptions.endDate,
                filterOptions.showFlaggedOnly,
                filterOptions.showFlaggedByUserOnly,
                filterOptions.status,
                this.props.integrationPartnerData.getGroupsFilter(),
                filterOptions.scoreIds,
                filterOptions.flaggedStatus,
                this.props.videoEvents.getIncludeInactiveDrivers(),
                this.props.videoEvents.getIncludeInactiveVehicles(),
                filterOptions.companyLevelIds,
            );

            const videoEventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();
            await this.props.videoEvents.getFilteredEventsMultiselect(videoEventFilter);
            await this.props.videoEvents.getListOfEventIds(videoEventFilter);

            const freezer = this.props.videoEvents.getState();
            const { videoEventResults } = freezer;

            this.setState({
                events: videoEventResults.data && videoEventResults.data.data ? videoEventResults.data.data : [],
            });
        }
    }

    renderCompanyLevelFilter(): React.ReactNode {
        if (!isEncompass || !this.props.isCompanyLevelSplitOn) {
            return null;
        }
        const { deviceResults } = this.props.integrationPartnerData.getState();
        const fleetName = this.props.integrationPartnerData.getCompanyName();
        const items = getFormattedCompanyLevels(deviceResults, fleetName);
        const eventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();

        return (
            <CompanyLevelFilter
                fleetName={fleetName}
                items={items}
                selectedValues={eventFilter.companyLevelIds || []}
                isMobileView={this.state.isMobileView}
                applyFilter={(companyLevelIds: string[]) => {
                    const current = this.props.videoEventFilterMultiselect.getVideoEventFilter();
                    this.handleFilterEventsMultiselect({ ...current, companyLevelIds });
                }}
            />
        );
    }

    @bind
    handleVideoEventsFilterChange(filterKey: string, value: any): void {
        const eventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();

        this.handleFilterEventsMultiselect({
            ...eventFilter,
            [filterKey]: value,
        });
    }

    @bind
    getVideoEventsFilterConfig(): FilterConfig[] {
        const eventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();

        const includeInactiveDrivers = this.props.videoEvents.getIncludeInactiveDrivers();
        const includeInactiveVehicles = this.props.videoEvents.getIncludeInactiveVehicles();

        const filterStartDate = toDate(eventFilter.startDate!);
        const filterEndDate = toDate(eventFilter.endDate!);

        const { videoEventTypeResults } = this.props.videoEvents.getState();
        const { deviceResults, driversResults } = this.props.integrationPartnerData.getState();

        const formattedEventTypes = getFormattedEventTypes(videoEventTypeResults);
        const formattedDrivers = getFormattedDrivers(driversResults, includeInactiveDrivers);
        const formattedDevices = getFormattedDeviceResults(deviceResults, filterStartDate, filterEndDate, includeInactiveVehicles);
        const formattedEventStatuses = getVideoEventStatusDropdownList();

        const disabled =
            formattedEventStatuses.length === 0 ||
            formattedEventTypes.length === 0 ||
            formattedDevices.length === 0 ||
            formattedDrivers.length === 0;

        const filterConfig: FilterConfig[] = [
            {
                type: FilterType.Multiselect,
                key: 'eventTypes',
                label: 'Event Type',
                items: formattedEventTypes,
                selectedValues: eventFilter.eventTypes || [],
                disabled: disabled,
                searchable: true,
            },
            {
                type: FilterType.Multiselect,
                key: 'driverNames',
                label: 'Driver',
                items: formattedDrivers,
                selectedValues: eventFilter.driverNames || [],
                disabled: disabled,
                searchable: true,
                toggleable: this.props.isShowInactiveDriversSplitOn,
                toggleText: isEncompass ? 'Include Terminated' : 'Include Inactive',
                toggleStatus: includeInactiveDrivers,
                onToggleChange: (checked: boolean) => {
                    this.props.videoEvents.setIncludeInactiveDrivers(checked);
                },
            },
            {
                type: FilterType.Multiselect,
                key: 'vehicleIds',
                label: 'Vehicle',
                items: formattedDevices,
                selectedValues: eventFilter.vehicleIds || [],
                disabled: disabled,
                searchable: true,
                toggleable: this.props.isShowInactiveVehiclesSplitOn,
                toggleText: 'Include Inactive',
                toggleStatus: includeInactiveVehicles,
                onToggleChange: (checked: boolean) => {
                    this.props.videoEvents.setIncludeInactiveVehicles(checked);
                },
            },
            {
                type: FilterType.Multiselect,
                key: 'status',
                label: 'Event Status',
                items: formattedEventStatuses,
                selectedValues: eventFilter.status || [],
                disabled: disabled,
            },
            {
                type: FilterType.Multiselect,
                key: 'scoreIds',
                label: 'Score',
                items: VideoEventScoreOptions,
                selectedValues: eventFilter.scoreIds || [],
                disabled: disabled,
                sortItemsByValue: true,
            },
            {
                type: FilterType.Multiselect,
                key: 'flaggedStatus',
                label: 'Flag',
                items: FlaggedStatusOptions,
                selectedValues: eventFilter.flaggedStatus || [],
                disabled: disabled,
            },
            {
                type: FilterType.DateRange,
                key: 'dateRange',
                startDate: eventFilter.startDate,
                endDate: eventFilter.endDate,
                onDateRangeApply: (startDate: any, endDate: any) => {
                    this.handleFilterEventsMultiselect({
                        ...eventFilter,
                        startDate: startDate,
                        endDate: endDate,
                    });
                },
            },
        ];

        return filterConfig;
    }

    @bind
    async _onPageNumberChanged(newPage: number): Promise<void> {
        this.props.videoEventFilterMultiselect.setPageNumber(newPage);
        const eventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();
        this.props.videoEvents.getFilteredEventsMultiselect(eventFilter);
    }

    @bind
    async gridSort(sortBy: string, sortAsc: boolean): Promise<void> {
        VideoEventService.clearVideoEvents();
        this.props.videoEventFilterMultiselect.setPageNumber(1);
        this.props.videoEventFilterMultiselect.setSortFields(sortBy, sortAsc);
        const eventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();
        await this.props.videoEvents.getFilteredEventsMultiselect(eventFilter);
    }

    @bind
    async loadVideoEventsOnScroll(): Promise<void> {
        if (!this.props.videoEvents.getState().videoEventResults.isFetching) {
            const eventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();
            const currentPage = eventFilter.currentPage;
            if (currentPage && currentPage !== undefined) {
                let newPage = currentPage;
                newPage++;
                setTimeout(async (): Promise<void> => {
                    await this._onPageNumberChanged(newPage);
                }, 1500);
            }
        }
    }

    @bind
    toggleEvents(): void {
        this.props.videoEventFilterMultiselect.toggleShowOnlySuccessfulVideoEvents();
        this.props.videoEventFilterMultiselect.setPageNumber(1);
        const eventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();
        VideoEventService.clearVideoEvents();
        this.props.videoEvents.getFilteredEventsMultiselect(eventFilter);
    }

    @bind
    flagEvent(eventId: number | undefined): void {
        if (!canEditByRole([RolesEnum.videoEventEdit, RolesEnum.encompassVideo])) return;
        this.props.videoEvents.flagVideoEvent(eventId).then(() => {
            const { videoEventResultsFinal } = this.props.videoEvents.getState();
            /* if the current list of events has an event matching this event id,
               then that means it was toggled off. if it was toggled off and the
               showFlaggedByUserOnly filter option is true, then the list needs 
               to be re-filtered.
            */
            const { id } = this.props.integrationPartnerData.getState().currentUserResult.data;
            const eventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();
            const event = _.find(videoEventResultsFinal, (event) => event.id === eventId);
            const flaggedByUsers = event?.flaggedByUsers || [];
            const shouldReloadVideoEvents =
                event &&
                (eventFilter.showFlaggedByUserOnly ||
                    (eventFilter.showFlaggedOnly && flaggedByUsers.length === 1 && flaggedByUsers[0].integrationProviderSourceId === id));
            if (shouldReloadVideoEvents) {
                VideoEventService.clearVideoEvents();
                this.props.videoEvents.getFilteredEventsMultiselect(eventFilter);
            }
        });
    }

    @bind
    onRateVideo(eventId: number | undefined, rating: number, status: string): void {
        if (this.state.hasVideoEventScoring) {
            this.props.videoEvents.postVideoEventRatings({ videoEventIdList: [eventId ?? 0], ratingValue: rating });
            if (status == 'New') {
                this.props.videoEvents.updateLocalVideoEventStatus('In_Progress', eventId);
            }
        }
    }

    @bind
    onClearRating(eventId: number | undefined): void {
        if (this.state.hasVideoEventScoring) {
            this.props.videoEvents.postVideoEventRatings({ videoEventIdList: [eventId ?? 0], ratingValue: 0 }); // rating value 0 clears the rating
        }
    }

    @bind
    onReopenEvent(eventId: number | undefined): void {
        if (this.state.hasVideoEventStatusWorkflow) {
            this.props.videoEvents.reopenEvent(eventId);
        }
    }

    @bind
    onDismissVideo(eventId: number | undefined): void {
        if (this.state.hasVideoEventStatusWorkflow) {
            this.props.videoEvents.dismissVideoEvent(eventId, 'Dismissed');
        }
    }

    @bind
    async handleBulkDismissVideo(body: BulkRequest | undefined): Promise<boolean> {
        if (this.state.hasVideoEventStatusWorkflow) {
            return this.props.videoEvents.dismissBulkVideoEvent(body);
        }
        return false;
    }

    @bind
    async handleSubmitBulkScore(body: BulkRatingRequest): Promise<boolean> {
        if (this.state.hasVideoEventStatusWorkflow) {
            return await this.props.videoEvents.postVideoEventRatings(body);
        }
        return false;
    }

    @bind
    async handleBulkAssign(req: AssignDriverToEventsRequest): Promise<boolean> {
        if (this.state.hasVideoEventStatusWorkflow) {
            const success = await this.props.videoEvents.assignBulkVideoEvent(req);

            if (success) {
                this.refreshVideoEventsList();
                this.setState({
                    showSnackbar: true,
                    snackbarType: AlertTypes.Success,
                    snackbarMessage: 'Driver assigned to event(s) successfully.',
                });
            } else {
                this.setState({
                    showSnackbar: true,
                    snackbarType: AlertTypes.Fail,
                    snackbarMessage: 'Unable to assign driver to event(s).',
                });
            }

            return success;
        }
        return false;
    }

    @bind
    async getFullListOfEventIds(): Promise<number[]> {
        if (this.state.hasVideoEventStatusWorkflow) {
            const videoEventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();
            return await this.props.videoEvents.getListOfEventIds(videoEventFilter);
        }
        return [];
    }

    @bind
    async handleVideoPlayed(eventId: number | undefined) {
        this.props.videoEvents.updateVideoEventWorkflowStatus(eventId, 'In_Progress');
        if (eventId) {
            const type = this.props.videoEvents.GetUploadTypeString('FrontVideo');
            await this.props.videoEvents.addVideoEventActionRecord(eventId, 'PlayedVideo', `${type} watched from video event page`);
        }
    }

    @bind
    calculateShrinkHeight(): number {
        let height = 0;
        if (this.videoEventsHeaderDiv) {
            height += this.videoEventsHeaderDiv.offsetHeight;
        }
        if (this.filterHeaderDiv) {
            height += this.filterHeaderDiv.offsetHeight;
        }
        if (this.filterBarDiv) {
            height += this.filterBarDiv.offsetHeight;
        }
        return height;
    }

    setShrinkHeight(): void {
        if (isMounted) {
            const height = this.calculateShrinkHeight();
            this.setState({ shrinkHeight: height });
        }
    }

    @bind
    refreshVideoEventsList(): void {
        this.props.videoEvents.refreshVideoEvents();
    }

    @bind
    refreshVideoEventsListForDisable(): void {
        this.props.videoEvents.refreshVideoEvents();
        const videoEventFilter = this.props.videoEventFilterMultiselect.getVideoEventFilter();
        this.props.videoEvents.getListOfEventIds(videoEventFilter);
    }

    @bind
    toggleDeletePopup(selectedId?: number): void {
        if (selectedId) {
            this.setState({ selectedEventIds: selectedId ? [selectedId] : [] });
        }
        this.setState({ showDeletePopup: !this.state.showDeletePopup, isBulk: false });
    }

    @bind
    toggleDismissPopup(selectedId?: number): void {
        if (selectedId) {
            this.setState({ selectedEventIds: selectedId ? [selectedId] : [] });
        }
        this.setState({ showDismissPopup: !this.state.showDismissPopup });
    }

    @bind
    changeToDisabled(disabled: any): void {
        this.setState({ deleteButtonDisabled: disabled });
    }

    @bind
    bulkDeleteDialogOpen(open: boolean, selectedIds: number[]): void {
        this.setState({ selectedEventIds: selectedIds, showDeletePopup: open, isBulk: true });
    }

    render(): JSX.Element {
        const freezer = this.props.videoEvents.getState();
        const { videoEventResults, videoEventResultsFinal, videoEventViewType, videoEventRatings } = freezer;
        const totalVideoRecords = videoEventResults.data && videoEventResults.data.data ? (videoEventResults.data.totalCount as number) : 0;
        const data = videoEventResultsFinal.length > 0 ? videoEventResultsFinal : [];
        const hasMorePagesToLoad =
            !freezer.hasInfiniteScrollError && (videoEventResults.data?.currentPage ?? 0) < (videoEventResults.data?.totalPages ?? 0)
                ? true
                : false;
        const { shrinkHeight, selectedEventIds, showDeletePopup, isBulk, showDismissPopup, showSnackbar, snackbarType, snackbarMessage } =
            this.state;

        const { videoEventFilterMultiselect } = this.props;

        const videoEventList = () => (
            <VideoEventListMultiSelect
                items={data}
                onGridSort={this.gridSort}
                onSelectVideoEvent={this.props.onSelectVideoEvent}
                loadNextPage={this.loadVideoEventsOnScroll}
                morePagesToLoad={hasMorePagesToLoad}
                onFlagVideoEvent={this.flagEvent}
                onRateVideo={this.onRateVideo}
                onDismissVideo={this.onDismissVideo}
                handleBulkDismissVideo={this.handleBulkDismissVideo}
                onReopenEvent={this.onReopenEvent}
                onClearRating={this.onClearRating}
                filters={this.props.videoEventFilterMultiselect.getVideoEventFilter()}
                onUpdateFilters={this.handleFilterEventsMultiselect}
                isMobileView={this.state.isMobileView}
                videoEventRatings={videoEventRatings.data!}
                handleSubmitBulkScore={this.handleSubmitBulkScore}
                handleBulkAssign={this.handleBulkAssign}
                pageNumber={videoEventFilterMultiselect.getVideoEventFilter().currentPage!}
                itemsPerPage={videoEventFilterMultiselect.getVideoEventFilter().itemsPerPage!}
                totalItemsCount={totalVideoRecords}
                getFullListOfEventIds={this.getFullListOfEventIds}
                refreshVideoEventsList={this.refreshVideoEventsList}
                toggleDeletePopup={this.toggleDeletePopup}
                deleteButtonDisabled={this.state.deleteButtonDisabled}
                bulkDeleteDialogOpen={this.bulkDeleteDialogOpen}
                toggleDismissPopup={this.toggleDismissPopup}
            />
        );

        return (
            <div className={styles.main}>
                <div ref={(ref) => (this.videoEventsHeaderDiv = ref)}>
                    <TermsOfUse />
                    <PageHeader
                        pageTitle={'Video Events'}
                        pageSubTitle={VideoEventHeaderSubText()}
                        fixedHeight={false}
                        rightContent={this.renderCompanyLevelFilter()}
                    ></PageHeader>
                </div>
                <div ref={(ref) => (this.filterHeaderDiv = ref)}>
                    <ConfigurableFilterBar
                        filters={this.getVideoEventsFilterConfig()}
                        onFilterChange={this.handleVideoEventsFilterChange}
                        onMobileViewChange={(isMobile) => this.setState({ isMobileView: isMobile })}
                        slotProps={{
                            mobile: {
                                left: <FiltersButton onClick={this.toggleFilters} />,
                                right: (
                                    <>
                                        <VideoEventExportComponent />
                                        <ViewModeToggleButton
                                            handleEventLayoutChange={this.handleEventLayoutChange}
                                            viewType={videoEventViewType}
                                        />
                                    </>
                                ),
                            },
                            desktop: {
                                right: (
                                    <>
                                        <VideoEventExportComponent />
                                        <ViewModeToggleButton
                                            handleEventLayoutChange={this.handleEventLayoutChange}
                                            viewType={videoEventViewType}
                                        />
                                    </>
                                ),
                            },
                        }}
                        mobileProps={{
                            drawerIsOpen: this.state.showMobileFilterDrawer,
                            onCloseDrawer: this.toggleFilters,
                            drawerTitle: 'Filter Videos',
                        }}
                    />
                </div>
                <div>
                    {data.length === 0 && (
                        <div>
                            <div className={styles.noVideos}>
                                {this.props.videoEvents.getVideosLoaded() ? (
                                    <div>No Video Events for the criteria selected</div>
                                ) : (
                                    <CircularProgress></CircularProgress>
                                )}
                            </div>
                        </div>
                    )}
                    {videoEventViewType === 'tile'
                        ? data.length > 0 && (
                              <VideoEventCardList
                                  items={data}
                                  onSelectVideoEvent={this.props.onSelectVideoEvent}
                                  loadNextPage={this.loadVideoEventsOnScroll}
                                  morePagesToLoad={hasMorePagesToLoad}
                                  onFlagVideoEvent={this.flagEvent}
                                  onDismissVideo={this.onDismissVideo}
                                  handleVideoPlayed={this.handleVideoPlayed}
                                  onReopenEvent={this.onReopenEvent}
                                  shrinkHeight={shrinkHeight}
                                  filters={this.props.videoEventFilterMultiselect.getVideoEventFilter()}
                                  onUpdateFilters={this.handleFilterEventsMultiselect}
                                  isMobileView={this.state.isMobileView}
                                  toggleDeletePopup={this.toggleDeletePopup}
                                  deleteButtonDisabled={this.state.deleteButtonDisabled}
                                  toggleDismissPopup={this.toggleDismissPopup}
                              />
                          )
                        : data.length > 0 && videoEventList()}
                </div>
                <TypedSplitTreatments names={[splitTreatmentNames.showUnProcessedEvents]} attributes={getSplitUserAttributes()}>
                    {({ treatments, isReady, isTimedout }: ISplitTreatmentsChildProps) => {
                        return isReady || isTimedout ? (
                            isSplitTreatmentOn(treatments[splitTreatmentNames.showUnProcessedEvents]) ? (
                                <Button className={styles.showUnprocessedEvents} onClick={this.toggleEvents}>
                                    {this.props.videoEventFilterMultiselect.getState().showVideoPipelineSuccessOnly
                                        ? 'Show Unprocessed Events'
                                        : 'Hide Unprocessed Events'}
                                </Button>
                            ) : (
                                <div></div>
                            )
                        ) : (
                            <div></div>
                        );
                    }}
                </TypedSplitTreatments>
                <DeleteConfirmation
                    eventIds={selectedEventIds}
                    showDeletePopup={showDeletePopup}
                    toggleDeletePopup={this.toggleDeletePopup}
                    changeToDisabled={this.changeToDisabled}
                    refreshVideoEvent={this.refreshVideoEventsList}
                    isBulk={isBulk}
                />
                <DismissConfirmation
                    eventIds={selectedEventIds}
                    showDeletePopup={showDismissPopup}
                    toggleDeletePopup={this.toggleDismissPopup}
                    changeToDisabled={this.changeToDisabled}
                    onDismissVideo={this.onDismissVideo}
                    refreshVideoEvent={this.refreshVideoEventsListForDisable}
                />
                <AlertMessage
                    openSnackbar={showSnackbar}
                    showIcon={!isEncompass}
                    alertType={snackbarType}
                    handleSnackClose={() => this.setState({ showSnackbar: false })}
                    message={snackbarMessage}
                    autoHide={true}
                    isUndo={true}
                    handleSubmit={() => this.setState({ showSnackbar: false })}
                    useNewIcons={true}
                />
            </div>
        );
    }
}

export const VideoEventsPage = PricingPlanService.inject(
    VideoEventFilterServiceMultiselect.inject(
        VideoEventService.inject(ConfigService.inject(IntegrationPartnerDataService.inject(_VideoEventsPage))),
    ),
);
